import React, { useState } from "react";
import {
  Save,
  Edit3,
  Plus,
  Settings,
  X,
  Check,
  Trash2,
  List,
  Sparkles,
  ExternalLink,
  Copy,
} from "lucide-react";
import EditableInput from "./common/EditableInput";

/* ─── AI Categorizer Prompt ──────────────────────────────────────────── */
// The prompt is built dynamically from the user's actual expense columns
// so that custom columns the user added (e.g., "Tithing", "Childcare",
// "Subscriptions") get included in the AI categorization, not just the
// CRA defaults. See buildCategorizerPrompt() below.

// Hand-tuned definitions for the common default columns. These give the
// AI better context for ambiguous transactions. Keys are the snake_case
// column name; if a column isn't in this map we generate a generic
// description from the human-readable label.
const COLUMN_HINTS = {
  rent:          "rent or mortgage payment",
  mortgage:      "mortgage payment, escrow, principal + interest",
  utilities:     "electric, gas, water, internet, phone, garbage",
  insurance:     "health, auto, home, life, dental insurance premiums",
  groceries:     "supermarkets, food markets, butchers — NOT restaurants",
  entertainment: "streaming, restaurants, bars, dining out, events, hobbies",
  travel:        "flights, hotels, fuel/gas, ride-share, public transit, parking",
  food:          "groceries and dining combined",
  dining:        "restaurants, bars, takeout, food delivery",
  transportation:"fuel/gas, ride-share, public transit, parking, vehicle costs",
  health:        "doctor visits, prescriptions, medical, dental, vision",
  childcare:     "daycare, school fees, childcare, babysitting",
  education:     "tuition, courses, books, learning materials",
  subscriptions: "recurring software, streaming, memberships",
  tithing:       "donations, charity, tithing, religious giving",
  giving:        "donations, gifts to others, charity",
  pets:          "vet visits, pet food, pet supplies",
  household:     "cleaning supplies, home repairs, furniture",
  shopping:      "clothing, general retail, online shopping",
  fitness:       "gym, supplements, sports, fitness equipment",
  taxes:         "tax payments, IRS, state tax",
  other:         "anything that does not fit above — be conservative; explain why",
};

const formatColumnNameForPrompt = (columnName) =>
  columnName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const buildCategorizerPrompt = (columns) => {
  const cols = Array.isArray(columns) && columns.length > 0
    ? columns
    : ["rent", "utilities", "insurance", "groceries", "entertainment", "travel", "other"];

  const categoryLines = cols
    .map((c) => {
      const label = formatColumnNameForPrompt(c);
      const hint = COLUMN_HINTS[c.toLowerCase()] || `transactions related to ${label.toLowerCase()}`;
      return `   - ${label} (${hint})`;
    })
    .join("\n");

  const summaryLines = cols.map((c) => `${formatColumnNameForPrompt(c)}: $X`).join("\n");

  return `You are a personal finance categorizer for my ARC F.I.R.E. Tracker.

I am going to attach (or paste) my bank or credit card statement. Please do the following:

1. Read every transaction line.
2. Categorize each one into EXACTLY ONE of these categories:
${categoryLines}
3. SKIP income, transfers between my own accounts, paid-off credit-card payments, and refunds.
4. Sum the totals for each category in the statement's currency.
5. Return ONLY this format, nothing else:

Month: [Month Year of statement]

${summaryLines}

Total Expenses: $X

Top 5 transactions by amount:
1. [Description] — $X — [Category]
2. ...

Anything you weren't sure about:
- [Description] — $X — [why uncertain]

Be precise with the math. If a transaction is ambiguous, put it in the closest matching category and flag it in the uncertain list. Do not invent transactions. Do not include my income or transfers.

Statement is attached / pasted below.`;
};

/* LLM deep-link config. Each entry tries a URL with a prefilled prompt
 * AND copies the prompt to clipboard as a universal fallback. URL prefill
 * support varies (Claude supports ?q=, ChatGPT supports ?q=, Gemini
 * does not reliably support prefill — so for Gemini we just open the
 * app and rely on clipboard paste). */
const LLM_TARGETS = {
  claude: {
    name: "Claude",
    accent: "#D97706", // amber/coral
    url: (prompt) =>
      `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    fallbackUrl: "https://claude.ai/new",
  },
  chatgpt: {
    name: "ChatGPT",
    accent: "#10A37F", // openai green
    url: (prompt) =>
      `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
    fallbackUrl: "https://chatgpt.com/",
  },
  gemini: {
    name: "Gemini",
    accent: "#4285F4", // google blue
    // Gemini does not reliably support URL-prefilled prompts. Just open
    // the app and rely on clipboard paste.
    url: () => "https://gemini.google.com/app",
    fallbackUrl: "https://gemini.google.com/app",
  },
};

/**
 * ExpensesTab – editable expense table with flexible month insertion
 *
 * Line items: each (month, category) cell can drill into a list of individual
 * purchases (e.g., "Costco $245", "Whole Foods $189"). When line items exist
 * for a cell, the cell value is auto-computed as the sum of items.
 */

const genItemId = () =>
  `i_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// Sum line items for a (row, field)
const sumItems = (row, field) => {
  const items = row?.items?.[field];
  if (!Array.isArray(items) || items.length === 0) return null;
  return items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
};

// Effective value: line-item sum overrides typed-in value when items exist
const cellValue = (row, field) => {
  const itemSum = sumItems(row, field);
  return itemSum != null ? itemSum : row?.[field] || 0;
};

const ExpensesTab = ({
  expenseData,
  expenseColumns = [
    "rent",
    "utilities",
    "insurance",
    "groceries",
    "entertainment",
    "travel",
    "other",
  ],
  editMode,
  onToggleEdit,
  updateExpenseData,
  addNextMonth,
  onUpdateColumns,
  setExpenseData,
}) => {
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [showMonthInsertModal, setShowMonthInsertModal] = useState(false);
  const [tempColumns, setTempColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");

  // Line-items drill-down modal: { rowIdx, field } when open, null when closed
  const [itemsModal, setItemsModal] = useState(null);
  const [draftDescription, setDraftDescription] = useState("");
  const [draftAmount, setDraftAmount] = useState("");

  // AI categorizer toast: { kind: "success"|"error", text }
  const [aiToast, setAiToast] = useState(null);

  /* ─── AI Categorizer launch handler ─────────────────────────────── */
  // 1. Copies the categorizer prompt to clipboard (universal fallback).
  // 2. Opens the LLM in a new tab with prompt prefilled where supported.
  // 3. Shows a small toast confirming the copy.
  const launchLLM = async (key) => {
    const target = LLM_TARGETS[key];
    if (!target) return;

    let copied = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(buildCategorizerPrompt(expenseColumns));
        copied = true;
      }
    } catch (_) {
      copied = false;
    }

    // Open the LLM. Try URL prefill first; fall back to bare URL on errors.
    let urlToOpen;
    try {
      urlToOpen = target.url(buildCategorizerPrompt(expenseColumns));
      // Some browsers cap URL length; if our encoded URL is huge, fall back.
      if (urlToOpen.length > 7500) urlToOpen = target.fallbackUrl;
    } catch (_) {
      urlToOpen = target.fallbackUrl;
    }

    window.open(urlToOpen, "_blank", "noopener,noreferrer");

    setAiToast({
      kind: "success",
      text: copied
        ? `Prompt copied. Paste it into ${target.name} and attach your statement.`
        : `Couldn't auto-copy. Use the "Copy prompt" button, then paste into ${target.name}.`,
    });

    // Auto-dismiss after 5 seconds
    setTimeout(() => setAiToast(null), 5000);
  };

  const copyPromptOnly = async () => {
    try {
      await navigator.clipboard.writeText(buildCategorizerPrompt(expenseColumns));
      setAiToast({
        kind: "success",
        text: "Prompt copied. Paste it into any AI of your choice.",
      });
    } catch (_) {
      setAiToast({
        kind: "error",
        text: "Couldn't copy. Select the prompt manually below.",
      });
    }
    setTimeout(() => setAiToast(null), 5000);
  };

  // For month insertion
  const [newMonthName, setNewMonthName] = useState("");
  const [insertPosition, setInsertPosition] = useState("end");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  /* ─── Line-item helpers ─────────────────────────────────────────── */
  const getItemsFor = (rowIdx, field) =>
    expenseData[rowIdx]?.items?.[field] || [];

  const writeItems = (rowIdx, field, newItems) => {
    setExpenseData((curr) => {
      const next = [...curr];
      const row = { ...next[rowIdx] };
      const items = { ...(row.items || {}) };
      if (newItems.length === 0) {
        delete items[field];
      } else {
        items[field] = newItems;
      }
      row.items = items;
      // Sync the numeric column to the new sum so the rest of the app sees it
      const sum = newItems.reduce(
        (s, it) => s + (parseFloat(it.amount) || 0),
        0
      );
      row[field] = sum;
      // Recompute total across all columns
      row.total = expenseColumns.reduce(
        (s, col) => s + (cellValue({ ...row, items: { ...items } }, col) || 0),
        0
      );
      next[rowIdx] = row;
      return next;
    });
  };

  const addLineItem = (rowIdx, field, description, amount) => {
    const cur = getItemsFor(rowIdx, field);
    const item = {
      id: genItemId(),
      description: description.trim() || "Item",
      amount: parseFloat(amount) || 0,
      date: new Date().toISOString().slice(0, 10),
    };
    writeItems(rowIdx, field, [...cur, item]);
  };

  const removeLineItem = (rowIdx, field, itemId) => {
    const cur = getItemsFor(rowIdx, field);
    writeItems(
      rowIdx,
      field,
      cur.filter((it) => it.id !== itemId)
    );
  };

  const updateLineItem = (rowIdx, field, itemId, updates) => {
    const cur = getItemsFor(rowIdx, field);
    writeItems(
      rowIdx,
      field,
      cur.map((it) => (it.id === itemId ? { ...it, ...updates } : it))
    );
  };

  const handleAddDraft = () => {
    if (!itemsModal) return;
    if (!draftAmount || parseFloat(draftAmount) <= 0) return;
    addLineItem(itemsModal.rowIdx, itemsModal.field, draftDescription, draftAmount);
    setDraftDescription("");
    setDraftAmount("");
  };

  /* ─── Helpers ────────────────────────────────────────────────────────── */
  const formatColumnName = (columnName) =>
    columnName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const formatColumnKey = (displayName) =>
    displayName
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  /* ─── Column Editor Logic ────────────────────────────────────────────── */
  const openColumnEditor = () => {
    setTempColumns([...expenseColumns]);
    setShowColumnEditor(true);
  };

  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    const columnKey = formatColumnKey(newColumnName.trim());
    if (tempColumns.includes(columnKey)) {
      alert("Column already exists!");
      return;
    }
    setTempColumns([...tempColumns, columnKey]);
    setNewColumnName("");
  };

  const handleRemoveColumn = (columnToRemove) =>
    setTempColumns(tempColumns.filter((col) => col !== columnToRemove));

  const handleSaveColumns = () => {
    if (onUpdateColumns) onUpdateColumns(tempColumns);
    setShowColumnEditor(false);
  };

  const handleCancelColumns = () => {
    setTempColumns([]);
    setNewColumnName("");
    setShowColumnEditor(false);
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...tempColumns];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newColumns.length) {
      [newColumns[index], newColumns[newIndex]] = [
        newColumns[newIndex],
        newColumns[index],
      ];
      setTempColumns(newColumns);
    }
  };

  /* ─── Month Insertion Logic ──────────────────────────────────────────── */
  const openMonthInsertModal = () => {
    setNewMonthName("");
    setInsertPosition("end");
    setSelectedMonthIndex(0);
    setShowMonthInsertModal(true);
  };

  const handleInsertMonth = () => {
    if (!newMonthName.trim()) {
      alert("Please enter a month name");
      return;
    }

    // Create new month object with all columns initialized to 0
    const newMonth = { month: newMonthName.trim(), total: 0 };
    expenseColumns.forEach((col) => {
      newMonth[col] = 0;
    });

    const updatedData = [...expenseData];
    
    if (insertPosition === "beginning") {
      updatedData.unshift(newMonth);
    } else if (insertPosition === "end") {
      updatedData.push(newMonth);
    } else if (insertPosition === "before") {
      updatedData.splice(selectedMonthIndex, 0, newMonth);
    } else if (insertPosition === "after") {
      updatedData.splice(selectedMonthIndex + 1, 0, newMonth);
    }

    setExpenseData(updatedData);
    setShowMonthInsertModal(false);
  };

  /* ─── Delete Month Handler ───────────────────────────────────────────── */
  const deleteMonth = (monthIndex) => {
    if (window.confirm("Are you sure you want to delete this month?")) {
      const updated = expenseData.filter((_, idx) => idx !== monthIndex);
      setExpenseData(updated);
    }
  };

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold">Expense Tracking</h3>

        <div className="flex gap-2">
          <button
            onClick={openColumnEditor}
            className="flex items-center gap-2 px-3 py-2 rounded-lg btn-outline border-arc-line"
          >
            <Settings size={16} /> Columns
          </button>

          <button
            onClick={openMonthInsertModal}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white hover:opacity-90"
          >
            <Plus size={16} /> Add Month
          </button>

          <button
            onClick={onToggleEdit}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              editMode ? "bg-green-600 text-white" : "bg-blue-600 text-white"
            } hover:opacity-90`}
          >
            {editMode ? <Save size={16} /> : <Edit3 size={16} />}{" "}
            {editMode ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      {/* Month Insert Modal */}
      {showMonthInsertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-arc-elevated rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Insert New Month</h4>
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="text-arc-dim hover:text-arc-muted"
              >
                <X size={20} />
              </button>
            </div>

            {/* Month Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Month Name (e.g., "Jan 2025", "February 2025")
              </label>
              <input
                type="text"
                value={newMonthName}
                onChange={(e) => setNewMonthName(e.target.value)}
                placeholder="Enter month name..."
                className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Insert Position
              </label>
              <select
                value={insertPosition}
                onChange={(e) => setInsertPosition(e.target.value)}
                className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginning">At the beginning</option>
                <option value="end">At the end</option>
                {expenseData.length > 0 && (
                  <>
                    <option value="before">Before a specific month</option>
                    <option value="after">After a specific month</option>
                  </>
                )}
              </select>
            </div>

            {/* Specific Month Selection (if before/after) */}
            {(insertPosition === "before" || insertPosition === "after") && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Select Month
                </label>
                <select
                  value={selectedMonthIndex}
                  onChange={(e) => setSelectedMonthIndex(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {expenseData.map((row, idx) => (
                    <option key={idx} value={idx}>
                      {row.month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="px-4 py-2 btn-outline border-arc-line rounded-lg hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertMonth}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Check size={16} /> Insert Month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Editor Modal */}
      {showColumnEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-arc-elevated rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">
                Customize Expense Columns
              </h4>
              <button
                onClick={handleCancelColumns}
                className="text-arc-dim hover:text-arc-muted"
              >
                <X size={20} />
              </button>
            </div>

            {/* Add New Column */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Add New Column
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Column name"
                  className="flex-1 px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === "Enter" && handleAddColumn()}
                />
                <button
                  onClick={handleAddColumn}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Current Columns */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Current Columns
              </label>
              <div className="space-y-2">
                {tempColumns.map((column, index) => (
                  <div
                    key={column}
                    className="flex items-center gap-2 p-2 bg-white/[0.03] rounded"
                  >
                    <span className="flex-1">{formatColumnName(column)}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveColumn(index, "up")}
                        disabled={index === 0}
                        className="px-2 py-1 text-sm bg-white/[0.08] rounded hover:bg-white/10 disabled:opacity-50"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveColumn(index, "down")}
                        disabled={index === tempColumns.length - 1}
                        className="px-2 py-1 text-sm bg-white/[0.08] rounded hover:bg-white/10 disabled:opacity-50"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => handleRemoveColumn(column)}
                        className="px-2 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancelColumns}
                className="px-4 py-2 btn-outline border-arc-line rounded-lg hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveColumns}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Check size={16} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── AI Statement Categorizer ──────────────────────────────── */}
      <div className="mb-6 rounded-2xl border border-arc-line bg-gradient-to-br from-arc-gold/[0.05] via-arc-gold/[0.02] to-transparent p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-arc-gold/10 border border-arc-gold/30 flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-arc-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow mb-1">AI Statement Categorizer</p>
            <h4 className="font-semibold text-arc-ink mb-1.5">
              Drop your bank statement into an AI — paste totals back here
            </h4>
            <p className="text-sm text-arc-muted leading-relaxed">
              Click an AI below. The categorizer prompt copies to your clipboard
              and the chat opens in a new tab. Paste the prompt, attach your
              bank or credit-card statement (CSV, PDF, or screenshot), and the
              AI returns clean category totals you can type into the table.
            </p>
          </div>
        </div>

        {/* LLM buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Claude */}
          <button
            onClick={() => launchLLM("claude")}
            className="btn-tactile group flex items-center gap-3 px-4 py-3 rounded-xl border border-arc-line bg-white/[0.02] hover:bg-white/[0.05] hover:border-arc-gold/40 transition-all"
            title="Open Claude with the categorizer prompt"
          >
            {/* Claude logo (Anthropic asterisk-style mark) */}
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#D97706" }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M12 2 L13.2 8.8 L19.2 5.4 L15.8 11.4 L22.6 12.6 L15.8 13.8 L19.2 19.8 L13.2 16.4 L12 23.2 L10.8 16.4 L4.8 19.8 L8.2 13.8 L1.4 12.6 L8.2 11.4 L4.8 5.4 L10.8 8.8 Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">Claude</p>
              <p className="text-[11px] text-arc-dim">Best with PDFs & docs</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-dim group-hover:text-arc-gold flex-shrink-0"
            />
          </button>

          {/* ChatGPT */}
          <button
            onClick={() => launchLLM("chatgpt")}
            className="btn-tactile group flex items-center gap-3 px-4 py-3 rounded-xl border border-arc-line bg-white/[0.02] hover:bg-white/[0.05] hover:border-arc-gold/40 transition-all"
            title="Open ChatGPT with the categorizer prompt"
          >
            {/* OpenAI logo (six-petal mark) */}
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#10A37F" }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M22.28 9.82a5.96 5.96 0 0 0-.51-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.99 4.07a5.96 5.96 0 0 0-3.99 2.9 6.05 6.05 0 0 0 .74 7.09 5.97 5.97 0 0 0 .51 4.91 6.06 6.06 0 0 0 6.52 2.91A5.99 5.99 0 0 0 13.27 24a6.06 6.06 0 0 0 5.77-4.21 5.98 5.98 0 0 0 3.99-2.9 6.06 6.06 0 0 0-.75-7.07Zm-9 12.59a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.78.78 0 0 0 .39-.68v-6.74L17.74 12c.02 0 .04.02.04.05v5.58a4.5 4.5 0 0 1-4.5 4.78ZM3.66 18.31a4.48 4.48 0 0 1-.54-3.02l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.34a.07.07 0 0 1-.03.06L9.8 19.95a4.5 4.5 0 0 1-6.14-1.64ZM2.5 8.94a4.48 4.48 0 0 1 2.34-1.97v5.69a.78.78 0 0 0 .39.68l5.81 3.35-2.02 1.17a.07.07 0 0 1-.07 0L4.11 15.07a4.5 4.5 0 0 1-1.61-6.13Zm16.51 3.83-5.84-3.39 2.02-1.16a.07.07 0 0 1 .07 0l4.83 2.79a4.5 4.5 0 0 1-.68 8.1v-5.66a.79.79 0 0 0-.4-.68Zm2.01-3.02-.14-.09-4.77-2.78a.77.77 0 0 0-.78 0L9.49 10.25V7.91a.07.07 0 0 1 .03-.06l4.83-2.78a4.5 4.5 0 0 1 6.67 4.66Zm-12.64 4.15-2.02-1.17a.07.07 0 0 1-.04-.05V7.13a4.5 4.5 0 0 1 7.38-3.45l-.14.08L8.78 6.52a.77.77 0 0 0-.39.68Zm1.1-2.36 2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">ChatGPT</p>
              <p className="text-[11px] text-arc-dim">Fast & widely available</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-dim group-hover:text-arc-gold flex-shrink-0"
            />
          </button>

          {/* Gemini */}
          <button
            onClick={() => launchLLM("gemini")}
            className="btn-tactile group flex items-center gap-3 px-4 py-3 rounded-xl border border-arc-line bg-white/[0.02] hover:bg-white/[0.05] hover:border-arc-gold/40 transition-all"
            title="Open Gemini with the categorizer prompt"
          >
            {/* Gemini logo (4-point star) */}
            <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#4285F4] to-[#9B72CB]">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M12 2 C12 7.5 16.5 12 22 12 C16.5 12 12 16.5 12 22 C12 16.5 7.5 12 2 12 C7.5 12 12 7.5 12 2 Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">Gemini</p>
              <p className="text-[11px] text-arc-dim">Free with Google account</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-dim group-hover:text-arc-gold flex-shrink-0"
            />
          </button>
        </div>

        {/* Copy-only fallback + toast */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={copyPromptOnly}
            className="btn-tactile flex items-center gap-2 text-xs text-arc-muted hover:text-arc-gold transition-colors"
          >
            <Copy size={12} />
            Copy prompt only
          </button>

          {aiToast && (
            <div
              className={`text-xs px-3 py-1.5 rounded-lg border ${
                aiToast.kind === "success"
                  ? "border-arc-gold/30 bg-arc-gold/5 text-arc-gold"
                  : "border-red-400/30 bg-red-500/5 text-red-300"
              }`}
            >
              {aiToast.text}
            </div>
          )}
        </div>
      </div>

      {/* Editable Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto">
          <thead>
            <tr className="bg-white/[0.03]">
              <th className="px-4 py-2 text-left">Month</th>
              {expenseColumns.map((column) => (
                <th key={column} className="px-4 py-2 text-right">
                  {formatColumnName(column)}
                </th>
              ))}
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {expenseData.map((row, idx) => (
              <tr key={`${row.month}-${idx}`} className="border-b">
                <td className="px-4 py-2 font-medium">{row.month}</td>

                {expenseColumns.map((field) => {
                  const itemSum = sumItems(row, field);
                  const hasItems = itemSum != null;
                  const display = hasItems ? itemSum : row[field] || 0;
                  const itemCount = (row?.items?.[field] || []).length;

                  return (
                    <td key={field} className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editMode ? (
                          hasItems ? (
                            <span
                              className="num text-arc-gold text-sm w-20 text-right"
                              title={`${itemCount} line item${itemCount === 1 ? "" : "s"}`}
                            >
                              {itemSum.toLocaleString()}
                            </span>
                          ) : (
                            <EditableInput
                              value={row[field] || 0}
                              onChange={(val) => updateExpenseData(idx, field, val)}
                              className="w-20 text-right"
                            />
                          )
                        ) : (
                          <span className="num">
                            {hasItems ? (
                              <span className="text-arc-gold">{display.toLocaleString()}</span>
                            ) : (
                              display.toLocaleString()
                            )}
                          </span>
                        )}
                        {editMode && (
                          <button
                            onClick={() => setItemsModal({ rowIdx: idx, field })}
                            className="btn-tactile p-1 rounded text-arc-dim hover:text-arc-gold relative"
                            title={hasItems ? `${itemCount} line items — click to edit` : "Add line items"}
                          >
                            <List size={14} />
                            {hasItems && (
                              <span className="absolute -top-1 -right-1 text-[9px] font-mono px-1 rounded-full bg-arc-gold text-arc-bg">
                                {itemCount}
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}

                <td className="px-4 py-2 text-right font-semibold text-red-600">
                  ${row.total.toLocaleString()}
                </td>

                {/* Delete button per month */}
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => deleteMonth(idx)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete Month"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Average + total summary */}
      {expenseData.length > 0 && (() => {
        const total = expenseData.reduce((s, r) => s + (r.total || 0), 0);
        const months = expenseData.length;
        const avg = months > 0 ? total / months : 0;
        const fmt = (n) =>
          `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
        return (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Avg Monthly Expense</p>
              <p className="text-2xl num text-arc-gold">{fmt(avg)}</p>
              <p className="text-xs text-arc-dim mt-1">across {months} months tracked</p>
            </div>
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Total Expenses</p>
              <p className="text-2xl num text-arc-ink">{fmt(total)}</p>
              <p className="text-xs text-arc-dim mt-1">all months combined</p>
            </div>
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Latest Month</p>
              <p className="text-2xl num text-arc-ink">{fmt(expenseData[expenseData.length - 1]?.total || 0)}</p>
              <p className="text-xs text-arc-dim mt-1">{expenseData[expenseData.length - 1]?.month || "—"}</p>
            </div>
          </div>
        );
      })()}

      {/* Column Info */}
      <div className="mt-4 text-sm text-arc-muted">
        <p>
          <strong>Current columns:</strong>{" "}
          {expenseColumns.map((col) => formatColumnName(col)).join(", ")}
        </p>
        <p className="mt-1">
          Click "Columns" to add, remove, or reorder expense categories. Click the
          list icon next to any cell to drill into individual line items (e.g.,
          "Costco $245, Whole Foods $189") that auto-sum into the cell total.
        </p>
      </div>

      {/* Line items drill-down modal */}
      {itemsModal && (() => {
        const row = expenseData[itemsModal.rowIdx];
        const field = itemsModal.field;
        const items = getItemsFor(itemsModal.rowIdx, field);
        const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        const fmt = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-arc-bg/80 backdrop-blur-sm"
              onClick={() => setItemsModal(null)}
            />
            <div className="relative surface-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="eyebrow mb-1">Line items</p>
                  <h3 className="headline text-xl">
                    {formatColumnName(field)} — {row?.month}
                  </h3>
                  <p className="text-arc-muted text-sm mt-1">
                    Add each transaction below. The cell total auto-sums.
                  </p>
                </div>
                <button
                  onClick={() => setItemsModal(null)}
                  className="btn-tactile p-2 rounded-lg text-arc-muted hover:text-arc-ink hover:bg-white/5"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Add new item */}
              <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-5">
                <p className="eyebrow mb-3">Add transaction</p>
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    value={draftDescription}
                    onChange={(e) => setDraftDescription(e.target.value)}
                    placeholder="e.g., Costco, Whole Foods, Trader Joe's"
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-sm"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      step={0.01}
                      value={draftAmount}
                      onChange={(e) => setDraftAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddDraft();
                      }}
                      placeholder="0.00"
                      className="w-32 pl-7 pr-3 py-2 rounded-lg text-sm num text-right"
                    />
                  </div>
                  <button
                    onClick={handleAddDraft}
                    disabled={!draftAmount || parseFloat(draftAmount) <= 0}
                    className="btn-tactile btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Add
                  </button>
                </div>
              </div>

              {/* Existing items */}
              {items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-arc-line bg-white/[0.02] p-8 text-center">
                  <p className="text-arc-muted text-sm">
                    No line items yet. Add your first transaction above.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 mb-5">
                  {items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center gap-3 rounded-xl border border-arc-line bg-white/[0.02] p-3"
                    >
                      <input
                        type="text"
                        value={it.description}
                        onChange={(e) =>
                          updateLineItem(itemsModal.rowIdx, field, it.id, {
                            description: e.target.value,
                          })
                        }
                        className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-sm bg-transparent border-transparent hover:border-arc-line focus:border-arc-line"
                      />
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          step={0.01}
                          value={it.amount}
                          onChange={(e) =>
                            updateLineItem(itemsModal.rowIdx, field, it.id, {
                              amount: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-28 pl-7 pr-3 py-1.5 rounded-lg text-sm num text-right"
                        />
                      </div>
                      <button
                        onClick={() => removeLineItem(itemsModal.rowIdx, field, it.id)}
                        className="btn-tactile p-1.5 rounded-lg text-arc-muted hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer total + close */}
              <div className="flex items-center justify-between pt-4 border-t border-arc-line">
                <div>
                  <p className="eyebrow mb-1">Category total</p>
                  <p className="text-2xl num text-arc-gold">{fmt(total)}</p>
                  <p className="text-xs text-arc-dim mt-0.5">
                    {items.length} item{items.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  onClick={() => setItemsModal(null)}
                  className="btn-tactile btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
                >
                  <Check size={14} strokeWidth={2.5} />
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ExpensesTab;